import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, FileText, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LABELS = {
  fr: { cgu: "Conditions Générales d'Utilisation", cgv: 'Conditions Générales de Vente', close: 'Fermer', version: 'Version' },
  en: { cgu: 'Terms of Use', cgv: 'Terms of Sale', close: 'Close', version: 'Version' },
  es: { cgu: 'Condiciones generales de uso', cgv: 'Condiciones generales de venta', close: 'Cerrar', version: 'Versión' },
  ar: { cgu: 'شروط الاستخدام', cgv: 'شروط البيع', close: 'إغلاق', version: 'الإصدار' },
  pt: { cgu: 'Termos de Uso', cgv: 'Termos de Venda', close: 'Fechar', version: 'Versão' },
  de: { cgu: 'Nutzungsbedingungen', cgv: 'Verkaufsbedingungen', close: 'Schließen', version: 'Version' },
  ru: { cgu: 'Условия использования', cgv: 'Условия продажи', close: 'Закрыть', version: 'Версия' },
  zh: { cgu: '使用条款', cgv: '销售条款', close: '关闭', version: '版本' },
  ja: { cgu: '利用規約', cgv: '販売条件', close: '閉じる', version: 'バージョン' },
  hi: { cgu: 'उपयोग की शर्तें', cgv: 'बिक्री की शर्तें', close: 'बंद करें', version: 'संस्करण' },
  nl: { cgu: 'Gebruiksvoorwaarden', cgv: 'Verkoopvoorwaarden', close: 'Sluiten', version: 'Versie' },
  sw: { cgu: 'Masharti ya Matumizi', cgv: 'Masharti ya Mauzo', close: 'Funga', version: 'Toleo' },
  ht: { cgu: 'Kondisyon Itilizasyon', cgv: 'Kondisyon Vant', close: 'Fèmen', version: 'Vèsyon' },
};
export const legalLabels = (l) => LABELS[l] || LABELS.en;

// Minimal markdown → blocks (headings, bullets, paragraphs) — no external dep
const renderMd = (md) => (md || '').split('\n').map((line, i) => {
  const t = line.trim();
  if (!t) return <div key={i} className="h-2" />;
  if (t.startsWith('### ')) return <h4 key={i} className="text-sm font-bold text-white mt-3">{t.slice(4)}</h4>;
  if (t.startsWith('## ')) return <h3 key={i} className="text-base font-bold text-fuchsia-200 mt-4">{t.slice(3)}</h3>;
  if (t.startsWith('# ')) return <h2 key={i} className="text-lg font-black text-white mt-2">{t.slice(2)}</h2>;
  if (t.startsWith('* ') || t.startsWith('- ')) return <p key={i} className="text-sm text-gray-200 pl-4 relative before:content-['•'] before:absolute before:left-0">{t.slice(2).replace(/\*\*/g, '')}</p>;
  return <p key={i} className="text-sm text-gray-200 leading-relaxed">{t.replace(/\*\*/g, '')}</p>;
});

export const LegalDocModal = ({ slug, onClose }) => {
  const { language } = useLanguage();
  const L = legalLabels(language);
  const [doc, setDoc] = useState(null);
  useEffect(() => { axios.get(`/api/legal/${slug}`).then((r) => setDoc(r.data)).catch(() => setDoc({ content_md: '—' })); }, [slug]);
  return createPortal(
    <div className="fixed inset-0 z-[10000] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="legal-doc-modal">
      <div className="w-full max-w-2xl bg-gray-950 border border-white/10 rounded-3xl flex flex-col max-h-[90vh] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-white/10 flex items-center gap-3">
          <FileText className="w-5 h-5 text-fuchsia-300" />
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-white truncate">{L[slug] || doc?.title}</h3>
            {doc?.version && <p className="text-[11px] text-gray-400">{L.version} {doc.version}</p>}
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" aria-label={L.close} data-testid="legal-doc-close"><X className="w-5 h-5 text-white" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-1" data-testid="legal-doc-content">
          {!doc ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-fuchsia-400" /></div> : renderMd(doc.content_md)}
        </div>
        <div className="p-3 border-t border-white/10">
          <button onClick={onClose} className="w-full py-2.5 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-semibold" data-testid="legal-doc-close-bottom">{L.close}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Footer links (Welcome page bottom)
export const LegalFooterLinks = () => {
  const { language } = useLanguage();
  const L = legalLabels(language);
  const [open, setOpen] = useState(null);
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-white/70" data-testid="legal-footer-links">
      <button onClick={() => setOpen('cgu')} className="underline underline-offset-2 hover:text-white" data-testid="legal-link-cgu">{L.cgu}</button>
      <span className="opacity-40">·</span>
      <button onClick={() => setOpen('cgv')} className="underline underline-offset-2 hover:text-white" data-testid="legal-link-cgv">{L.cgv}</button>
      {open && <LegalDocModal slug={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

export default LegalFooterLinks;
